import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  SelectChangeEvent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { SelectedAccount } from '../../components/SelectAccount/SelectAccount';
import { config } from '../../pages/AccountWiseBreakup/config';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { useFetchData } from '../../hooks/useFetchData';
import { useSelector } from 'react-redux';
import { accountState } from '../../store';
import { format } from 'date-fns';
import CustomButton from '../../components/MasterComponent/Button/CustomButton';
import { exportToFile } from '../../utils/formateXLSX';
import { ALL_VALUE } from '../../pages/Invoice/config';
import BillingExcelDownloadButton from '../../pages/AccountWiseBreakup/BillingExcelDownloadButton';
import CustomSnackBar from '../MasterComponent/CustomSnackbar/CustomSnackBar';

interface ViewModeConfig {
  key: string;
  label: string;
  buttonText: string;
}

interface ReusableBillingHeaderProps {
  viewModes: ViewModeConfig[];
  currentViewMode: string;
  selectedMonth: string;
  onViewModeChange: (viewMode: string) => void;
  onMonthChange: (event: SelectChangeEvent) => void;
  onAccountChange: (data: any) => void;
  isSubmitting: boolean;
}

const BillingHeader: React.FC<ReusableBillingHeaderProps> = ({
  viewModes,
  currentViewMode,
  selectedMonth,
  onViewModeChange,
  onMonthChange,
  onAccountChange,
  isSubmitting,
}) => {
  const selectedAccountInfo = useSelector(accountState);
  const [selectedAccountId, setSelectedAccountId] = useState<any>(
    selectedAccountInfo.selectedAccountId
  );
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'info' | 'success' | 'error' | 'warning'
  >('info');
  const [isDownloading, setIsDownloading] = useState(false);

  const getLastMonths = (count = 10) => {
    const months = [];
    const today = new Date();

    for (let i = 0; i < count; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      months.push(`${monthName} ${date.getFullYear()}`);
    }

    return months;
  };

  const monthOptions = getLastMonths();

  const reqBodyRef = useRef({
    view: currentViewMode,
    month: format(new Date(selectedMonth), 'yyyy-MM-01'),
    formate: 'detailed',
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const {
    fetch: fetchCostReport,
    data: fetchCostReportDownloadStatus,
    loader: isReportDownloading,
    error: fetchCostReportDownloadError,
  } = useFetchData({
    fetchData: () => config.downloadAccountWiseReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  useEffect(() => {
    if (!isReportDownloading && isDownloading) {
      if (fetchCostReportDownloadError) {
        const errorMessage =
          fetchCostReportDownloadError?.error?.message ||
          'An error occurred while exporting.';
        setSnackBarMessage(errorMessage);
        setSnackbarSeverity('error');
      } else if (fetchCostReportDownloadStatus) {
        const { fileBuffer, fileName, message } = fetchCostReportDownloadStatus;

        if (fileBuffer && fileName) {
          exportToFile(fileBuffer, fileName);
          setSnackBarMessage('File downloaded successfully!');
          setSnackbarSeverity('success');
        } else if (message === 'No data available to export') {
          setSnackBarMessage('No data available to export.');
          setSnackbarSeverity('info');
        }
      }

      // Reset downloading state
      setIsDownloading(false);
    }
  }, [
    isReportDownloading,
    fetchCostReportDownloadStatus,
    fetchCostReportDownloadError,
    currentViewMode,
    selectedMonth,
    selectedAccountId,
    isDownloading,
  ]);

  const handleAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS) {
      setSelectedAccountId(data.accountId);
    }
    onAccountChange(data);
  };

  const handleExcelDownload = (reportType: string) => {
    setIsDownloading(true);
    setSnackBarMessage('');
    reqBodyRef.current = {
      view: currentViewMode,
      month: format(new Date(selectedMonth), 'yyyy-MM-01'),
      formate: reportType,
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
    fetchCostReport();
  };

  useEffect(() => {
    reqBodyRef.current = {
      view: currentViewMode,
      month: format(new Date(selectedMonth), 'yyyy-MM-01'),
      formate: 'detailed',
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
  }, [currentViewMode, selectedMonth, selectedAccountId]);

  return (
    <>
      <Box
        className="headerContainerRight"
        display="flex"
        alignItems="center"
        gap={1.25}
        flexWrap="wrap"
      >
        <FormControl
          variant="outlined"
          size="small"
          className="month-dropdown-container"
        >
          <InputLabel id="month-label" className="month-label"
            style={{
              opacity: isSubmitting ? 0.65 : 1,
            }}>
            Month
          </InputLabel>
          <Select
            labelId="month-label"
            id="month-select"
            value={selectedMonth}
            onChange={onMonthChange}
            label="Month"
            MenuProps={{
              PaperProps: {
                className: 'autocompletePaper',
                style: {
                  minWidth: '136px',
                  maxWidth: 'max-content',
                  whiteSpace: 'nowrap',
                  background: 'white',
                },
              },
            }}
            className="selectDropdown month-filter"
            style={{
              pointerEvents: isSubmitting ? 'none' : 'auto',
              color: isSubmitting ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
              backgroundColor: isSubmitting ? 'transparent' : 'inherit',
            }}
          >
            {monthOptions.map((month) => (
              <MenuItem key={month} value={month}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {viewModes.map((viewMode) => {
          if (currentViewMode !== viewMode.key) {
            return (
              <CustomButton
                key={viewMode.key}
                buttonType="outlined"
                onClickHandler={() => onViewModeChange(viewMode.key)}
                disabled={isSubmitting}
              >
                {viewMode.buttonText}
              </CustomButton>
            );
          }
          return null;
        })}

        <BillingExcelDownloadButton
          handleExcelDownload={handleExcelDownload}
          isDownloading={isReportDownloading}
          disabled={isSubmitting}
          size="small"
          variant="outlined"
          color="primary"
        />

        <SelectedAccount onAccountChange={handleAccountChange} />
      </Box>
      <CustomSnackBar
        message={snackBarMessage}
        severity={snackbarSeverity}
        autoShow={!!snackBarMessage}
      />
    </>
  );
};

export default BillingHeader;
